import '@fontsource/poppins';
import axios from 'axios';
import dayjs from 'dayjs';

import {
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
} from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

import React, { useEffect, useState } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { ViewButton } from '@src/components/buttons';
import Container from '@src/components/commons/containerPage.common';
import { useAuth } from '@src/contexts/authContext';

import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

const url = `${process.env.REACT_APP_API_URL}/ww-platform/admin/v2/organization/license/search`;

export default function OrganisationLicensePage() {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const { auth } = useAuth();
  const path = useLocation().pathname;
  const format_date = { year: 'numeric', month: 'short', day: 'numeric' };
  const handleClickDetail = (licenseId) => {
    navigate(`/detail-license/${licenseId}`);
  };
  const [loading, setLoading] = useState();

  const [startDate, setStartDate] = useState(null);
  const [startFrom, setStartFrom] = useState(null);
  const [expiredDate, setExpiredDate] = useState(null);
  const [expTo, setExpTo] = useState(null);

  const [searchName, setSearchName] = useState('');
  const [searchId, setSearchId] = useState('');
  const [searchStatus, setSearchStatus] = useState('');
  const [searchType, setSearchType] = useState('');
  const [searchActive, setSearchActive] = useState();

  const clearSearch = () => {
    setSearchName('');
    setSearchId('');
    setSearchStatus('');
    setSearchActive('');
    setSearchType('');
    setStartDate(null);
    setStartFrom(null);
    setExpiredDate(null);
    setExpTo(null);
  };

  const handleName = (event) => {
    setSearchName(event.target.value);
  };

  const handleId = (event) => {
    setSearchId(event.target.value);
  };

  const handleStatus = (event) => {
    setSearchStatus(event.target.value);
  };

  const handleType = (event) => {
    setSearchType(event.target.value);
  };

  const handleActive = (event) => {
    setSearchActive(event.target.value);
  };

  const handleStartDate = (newValue) => {
    setStartDate(newValue ? dayjs(newValue) : null);
  };

  const handleFrom = (newValue) => {
    setStartFrom(newValue ? dayjs(newValue) : null);
  };

  const handleExpDate = (newValue) => {
    setExpiredDate(newValue ? dayjs(newValue) : null);
  };

  const handleTo = (newValue) => {
    setExpTo(newValue ? dayjs(newValue) : null);
  };

  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleSearch = () => {
    if (auth?.idToken) {
      setLoading(true);
      const requestData = {
        filter: {
          organization_name: searchName,
          organization_code: searchId,
          organization_status: searchStatus,
          license_active_status: searchActive,
          license_start_date: {
            from: startDate || '',
            to: startFrom || '',
          },
          license_expired_date: {
            from: expiredDate || '',
            to: expTo || '',
          },
          license_package_type: searchType,
        },
        pagination: {
          page: 1,
          page_size: 1000,
        },
      };

      axios
        .post(`${url}`, requestData, {
          withCredentials: true,
          headers: {
            Authorization: `Bearer ${auth?.idToken}`,
          },
        })
        .then((response) => {
          setList(response.data.response.data);
          setLoading(false);
        })
        .catch((error) => {
          setLoading(false);
          console.log('Error', error.message);
          toast.error('No data found', {
            // position: 'top-center',
            autoClose: 2000,
          });
        });
    }
  };

  useEffect(() => {
    handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    searchName,
    searchId,
    searchStatus,
    searchActive,
    startDate,
    startFrom,
    expiredDate,
    expTo,
    searchType,
  ]);

  return (
    <div>
      <div>
        <Container pathUrl={path} />
      </div>
      <div className='flex justify-start px-8'>
        <Box
          sx={{ width: '100%', height: 'auto' }}
          className='w-[98%] rounded-xl bg-white px-4'
        >
          <div className='flex px-8 py-4'>
            <div>
              <div className='font-poppins py-2 text-sm text-gray-400'>
                Organisation Name
              </div>
              <TextField
                fullWidth
                size='medium'
                variant='outlined'
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'white',
                    height: '46px',
                  },
                  mt: '5px',
                  width: '250px',
                }}
                value={searchName}
                onChange={handleName}
                placeholder='Search by name'
              />
            </div>
            &nbsp; &nbsp; &nbsp;
            <div>
              <div className='font-poppins py-2 text-sm text-gray-400'>
                Organisation ID
              </div>
              <TextField
                fullWidth
                size='medium'
                variant='outlined'
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'white',
                    height: '46px',
                  },
                  mt: '5px',
                  width: '210px',
                }}
                value={searchId}
                onChange={handleId}
                placeholder='Search by ID'
              />
            </div>
            &nbsp; &nbsp; &nbsp;
            <div>
              <div className='font-poppins py-2 text-sm text-gray-400'>
                Organisation Status
              </div>
              <Select
                fullWidth
                displayEmpty
                sx={{
                  height: '46px',
                  backgroundColor: 'white',
                  mt: 0.5,
                  mb: '16px',
                  borderRadius: 2,
                  width: '150px',
                }}
                MenuProps={{
                  disablePortal: true,
                  PaperProps: {
                    style: {
                      zIndex: 1302,
                    },
                  },
                }}
                value={searchStatus}
                onChange={handleStatus}
              >
                <MenuItem disabled value=''>
                  <em style={{ color: '#8C8C8C' }}>Select</em>
                </MenuItem>

                <MenuItem value=''>ALL</MenuItem>
                <MenuItem value='created'>Created</MenuItem>
                <MenuItem value='verified'>Verified</MenuItem>
                <MenuItem value='banned'>Banned</MenuItem>
                <MenuItem value='deleted'>Deleted</MenuItem>
              </Select>
            </div>
            &nbsp; &nbsp; &nbsp;
            <div>
              <div className='font-poppins py-2 text-sm text-gray-400'>
                License Type
              </div>
              <Select
                fullWidth
                displayEmpty
                sx={{
                  height: '46px',
                  backgroundColor: 'white',
                  mt: 0.5,
                  mb: '16px',
                  borderRadius: 2,
                  width: '150px',
                }}
                MenuProps={{
                  disablePortal: true,
                  PaperProps: {
                    style: {
                      zIndex: 1302,
                    },
                  },
                }}
                value={searchType}
                onChange={handleType}
              >
                <MenuItem disabled value=''>
                  <em style={{ color: '#8C8C8C' }}>Select</em>
                </MenuItem>

                <MenuItem value=''>ALL</MenuItem>
                <MenuItem value='trial'>Trial</MenuItem>
                <MenuItem value='paid'>Paid</MenuItem>
              </Select>
            </div>
          </div>
          <div className='flex px-8'>
            <div>
              <div className='font-poppins py-2 text-sm text-gray-400'>
                Active Status
              </div>
              <Select
                fullWidth
                displayEmpty
                sx={{
                  height: '46px',
                  backgroundColor: 'white',
                  mt: 0.5,
                  mb: '16px',
                  borderRadius: 2,
                  width: '120px',
                }}
                MenuProps={{
                  disablePortal: true,
                  PaperProps: {
                    style: {
                      zIndex: 1302,
                    },
                  },
                }}
                value={searchActive || ''}
                onChange={handleActive}
              >
                <MenuItem disabled value=''>
                  <em style={{ color: '#8C8C8C' }}>Select</em>
                </MenuItem>

                <MenuItem value=''>ALL</MenuItem>
                <MenuItem value='true'>True</MenuItem>
                <MenuItem value='false'>False</MenuItem>
              </Select>
            </div>
            &nbsp; &nbsp; &nbsp;
            <div>
              <div className='font-poppins py-2.5 text-sm text-gray-400'>
                Start Date
              </div>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <div className='font-poppins w-3/3 block rounded-md border-0 py-2.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-1 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6'>
                  <DatePicker
                    className='px-2 text-xs'
                    reduceAnimations
                    value={startDate}
                    onChange={handleStartDate}
                    sx={{ width: '150px' }}
                    format='YYYY-MM-DD'
                    slotProps={{
                      textField: {
                        variant: 'outlined',
                        InputProps: {
                          sx: {
                            fontSize: '14px',
                            '& > input': {
                              padding: '3px 2px',
                            },
                            '& > fieldset': {
                              border: 'none',
                            },
                          },
                        },
                      },
                    }}
                  />
                  <DatePicker
                    className='px-2 text-xs'
                    reduceAnimations
                    value={startFrom}
                    onChange={handleFrom}
                    sx={{ width: '150px' }}
                    format='YYYY-MM-DD'
                    slotProps={{
                      textField: {
                        variant: 'outlined',
                        InputProps: {
                          sx: {
                            fontSize: '14px',
                            '& > input': {
                              padding: '3px 2px',
                            },
                            '& > fieldset': {
                              border: 'none',
                            },
                          },
                        },
                      },
                    }}
                  />
                </div>
              </LocalizationProvider>
            </div>
            &nbsp; &nbsp; &nbsp;
            <div>
              <div className='font-poppins py-2.5 text-sm text-gray-400'>
                Expired Date
              </div>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <div className='font-poppins w-3/3 block rounded-md border-0 py-2.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-1 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6'>
                  <DatePicker
                    className='px-2 text-xs'
                    reduceAnimations
                    value={expiredDate}
                    onChange={handleExpDate}
                    sx={{ width: '180px' }}
                    format='YYYY-MM-DD'
                    slotProps={{
                      textField: {
                        variant: 'outlined',
                        InputProps: {
                          sx: {
                            fontSize: '14px',
                            '& > input': {
                              padding: '3px 2px',
                            },
                            '& > fieldset': {
                              border: 'none',
                            },
                          },
                        },
                      },
                    }}
                  />
                  <DatePicker
                    className='px-2 text-xs'
                    reduceAnimations
                    value={expTo}
                    onChange={handleTo}
                    sx={{ width: '180px' }}
                    format='YYYY-MM-DD'
                    slotProps={{
                      textField: {
                        variant: 'outlined',
                        InputProps: {
                          sx: {
                            fontSize: '14px',
                            '& > input': {
                              padding: '3px 2px',
                            },
                            '& > fieldset': {
                              border: 'none',
                            },
                          },
                        },
                      },
                    }}
                  />
                </div>
              </LocalizationProvider>
            </div>
          </div>

          <div className='flex justify-end px-16 py-4'>
            <Button
              variant='contained'
              size='small'
              className='bg-cyan-600'
              sx={{
                width: 90,
                height: 36,
                textTransform: 'none',
                // backgroundColor: '#06b6d4', // cyan
                color: '#fff',
                fontWeight: 600,
                borderRadius: '8px',
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: '#0891b2',
                  boxShadow: 'none',
                },
              }}
              onClick={handleSearch}
            >
              Filter
            </Button>
            &nbsp;&nbsp;
            <Button
              variant='contained'
              size='small'
              sx={{
                width: 90,
                height: 36,
                textTransform: 'none',
                backgroundColor: '#ef4444', // red
                color: '#fff',
                fontWeight: 600,
                borderRadius: '8px',
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: '#dc2626',
                  boxShadow: 'none',
                },
              }}
              onClick={clearSearch}
            >
              Clear
            </Button>
          </div>
          <br />
        </Box>
      </div>
      <Box sx={{ width: '100%' }} className='px-8 py-10'>
        {loading ? (
          <div className='font-poppins py-6 px-8 text-gray-500'>Loading...</div>
        ) : (
          <span className='flex w-[98%] flex-col items-start'>
            <TableContainer
              className='rounded-xl'
              component={Paper}
              style={{
                width: '100%',
                paddingBottom: '12px',
                paddingLeft: '12px',
                paddingRight: '12px',
              }}
            >
              <Table sx={{ minWidth: 650 }} aria-label='simple table'>
                <TableHead>
                  <TableRow>
                    <TableCell
                      className='text-xs font-bold text-gray-400'
                      style={{ width: '300px' }}
                    >
                      Name
                    </TableCell>
                    <TableCell
                      className='text-xs font-bold text-gray-400'
                      style={{ padding: 0, width: '130px' }}
                    >
                      ID
                    </TableCell>
                    <TableCell
                      className='text-xs font-bold text-gray-400'
                      style={{ padding: 0, width: '100px' }}
                    >
                      Status
                    </TableCell>
                    <TableCell
                      className='text-xs font-bold text-gray-400'
                      style={{ padding: 0, width: '80px' }}
                    >
                      Type
                    </TableCell>
                    <TableCell
                      className='text-xs font-bold text-gray-400'
                      style={{ padding: 0, width: '80px' }}
                    >
                      Is Active
                    </TableCell>
                    <TableCell
                      className='text-xs font-bold text-gray-400'
                      style={{ padding: 0, width: '170px' }}
                    >
                      Start At
                    </TableCell>
                    <TableCell
                      className='text-xs font-bold text-gray-400'
                      style={{ padding: 0, width: '170px' }}
                    >
                      Expired At
                    </TableCell>
                    <TableCell
                      className='text-xs font-bold text-gray-400'
                      style={{ padding: 4, width: '150px' }}
                    >
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Array.isArray(list) && list.length > 0 ? (
                    list
                      .slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage,
                      )
                      .map((row) => (
                        <TableRow
                          hover
                          role='checkbox'
                          tabIndex={-1}
                          key={row.code}
                          sx={{
                            '&:last-child td, &:last-child th': { border: 0 },
                          }}
                        >
                          <TableCell
                            component='th'
                            scope='row'
                            className='border-white text-sm font-bold text-gray-500'
                          >
                            <span
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                              }}
                            >
                              {row?.organization_name}
                            </span>
                          </TableCell>
                          <TableCell
                            className='border-white text-sm font-bold text-gray-500'
                            style={{ padding: 0 }}
                          >
                            {row?.organization_code}
                          </TableCell>
                          <TableCell
                            className='border-white text-sm font-bold text-gray-500'
                            style={{ padding: 0 }}
                          >
                            {row.organization_status}
                          </TableCell>
                          <TableCell
                            className='border-white text-sm font-bold text-gray-500'
                            style={{ padding: 0 }}
                          >
                            {row?.license_package_type}
                          </TableCell>
                          <TableCell
                            className='border-white text-sm font-bold text-gray-500'
                            style={{ padding: 0 }}
                          >
                            {row?.license_is_active ? 'true' : 'false'}
                          </TableCell>
                          <TableCell
                            className='border-white text-sm font-bold text-gray-500'
                            style={{ padding: 0 }}
                          >
                            {row.license_start_at ? (
                              <>
                                {new Date(
                                  row.license_start_at,
                                ).toLocaleDateString('au-AU', format_date)}
                              </>
                            ) : (
                              'No date'
                            )}
                          </TableCell>
                          <TableCell
                            className='border-white text-sm font-bold text-gray-500'
                            style={{ padding: 0 }}
                          >
                            {row.license_expired_at ? (
                              <>
                                {new Date(
                                  row.license_expired_at,
                                ).toLocaleDateString('au-AU', format_date)}
                              </>
                            ) : (
                              'No date'
                            )}
                          </TableCell>
                          <TableCell
                            className='border-white text-sm font-bold text-gray-500'
                            style={{ padding: 0 }}
                          >
                            <div className='flex'>
                              <ViewButton
                                onClick={() =>
                                  handleClickDetail(row?.license_id)
                                }
                              />
                              &nbsp; &nbsp;
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                  ) : (
                    <TableRow>
                      <TableCell
                        className='border border-white'
                        colSpan={6}
                        align='center'
                      >
                        <div className='font-poppins py-6 text-gray-500'>
                          No organisations listed
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <Box
                className='flex justify-end py-3'
                sx={{ width: '100%', fontFamily: 'Poppins' }}
              >
                <TablePagination
                  rowsPerPageOptions={[10, 25, 100]}
                  component='div'
                  count={list.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </Box>
            </TableContainer>
          </span>
        )}
      </Box>
    </div>
  );
}
